import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { proxiedImage } from "@/lib/api";
import { toEmbedURL } from "@/components/PitchSchedule";

const DAY_LABELS = {
  "2026-06-16": "Tuesday, June 16",
  "2026-06-17": "Wednesday, June 17",
  "2026-06-18": "Thursday, June 18",
};

/**
 * Netflix-style horizontal scroller of available replays.
 * Surfaces only businesses that have replay_link OR video_url populated.
 * Each card links to /replays#<id> so the dedicated page deep-scrolls to the right video.
 */
export default function FeaturedReplays({ businesses = [], externalVotingUrl }) {
  const replays = useMemo(
    () =>
      (businesses || [])
        .filter((b) => b.replay_link || b.video_url)
        .filter((b) => b.schedule_status !== "Tentative")
        .slice(0, 12),
    [businesses]
  );

  if (replays.length === 0) return null;

  return (
    <section
      data-testid="featured-replays"
      className="bg-emerald-50 border-y border-emerald-200 py-12 sm:py-16"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit] font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
              Featured Replays
            </div>
            <h2 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl text-emerald-950 mt-2 leading-tight">
              Catch every pitch you missed.
            </h2>
            <p className="font-[Outfit] text-sm sm:text-base text-emerald-900/70 mt-2 max-w-2xl">
              Watch the entrepreneurs, hear their stories, then back the ones moving you with a $2 vote.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              asChild
              data-testid="featured-replays-all"
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 h-11 font-[Outfit] font-semibold"
            >
              <Link to="/replays">View All Replays →</Link>
            </Button>
            <Button
              asChild
              data-testid="featured-replays-vote"
              className="rounded-full bg-rose-500 hover:bg-rose-600 text-white px-5 h-11 font-[Outfit] font-semibold"
            >
              <a href={externalVotingUrl} target="_blank" rel="noreferrer">Cast Your $2 Vote</a>
            </Button>
          </div>
        </div>

        {/* Horizontal scroller */}
        <div
          className="flex gap-4 sm:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 lg:-mx-10 lg:px-10"
          data-testid="featured-replays-scroller"
        >
          {replays.map((b) => {
            const url = b.replay_link || b.video_url;
            const embed = toEmbedURL(url);
            const businessName = b.business_name || "";
            // YouTube thumbnail fallback
            let thumb = b.photo_url;
            const ytMatch = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
            if (!thumb && ytMatch) thumb = `https://i.ytimg.com/vi/${ytMatch[1]}/hqdefault.jpg`;

            return (
              <Link
                key={b.id}
                to={`/replays#${encodeURIComponent(b.id)}`}
                data-testid={`featured-replay-${b.id}`}
                className="relative shrink-0 w-72 sm:w-80 snap-start group block rounded-xl overflow-hidden bg-emerald-950 border-2 border-transparent hover:border-amber-400 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl"
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-emerald-900 relative overflow-hidden">
                  {thumb ? (
                    <img
                      src={proxiedImage(thumb)}
                      alt={b.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center font-[Cormorant_Garamond] text-3xl text-amber-300">
                      {b.name?.[0] || "•"}
                    </div>
                  )}
                  {/* Play badge */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-white/95 group-hover:bg-amber-300 flex items-center justify-center shadow-lg transition-colors">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-950 ml-1">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  {/* Day badge */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center bg-amber-300 text-emerald-950 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-[Outfit] font-bold">
                      Replay · {DAY_LABELS[b.pitch_day]?.split(",")[1]?.trim() || ""}
                    </span>
                  </div>
                  {!embed && (
                    <div className="absolute bottom-3 right-3">
                      <span className="inline-flex items-center bg-slate-900/80 text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full font-[Outfit] font-bold">
                        Opens external
                      </span>
                    </div>
                  )}
                </div>
                {/* Caption */}
                <div className="p-4 bg-white">
                  <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 leading-tight group-hover:text-emerald-700 transition-colors">
                    {b.name}
                  </div>
                  {businessName && (
                    <div className="font-[Outfit] text-xs text-emerald-900/60 mt-1 truncate">
                      {businessName}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Hint to swipe (mobile) */}
        <div className="text-center mt-2 text-xs text-emerald-900/50 font-[Outfit] sm:hidden">
          ← swipe for more →
        </div>
      </div>
    </section>
  );
}
